import React, { useMemo, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import ContactForm from '../components/ContactForm';
import CmsImage from '../components/CmsImage';
import { fetchLocalizedCollection } from '../lib/cms';

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const presserParam = searchParams.get('presser');
  const [companyInfo, setCompanyInfo] = useState(null);
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    fetchLocalizedCollection('virksomhedsoplysninger', 'da').then((items) => {
      if (items && items.length) setCompanyInfo(items[0]);
    });
    fetchLocalizedCollection('sider', 'da').then((items) => {
      const getSlugVal = (it) =>
        typeof it?.slug === 'object'
          ? it.slug.da || it.slug._ || it.slug.en || Object.values(it.slug)[0]
          : it?.slug;
      const match = (items || []).find(
        (s) => getSlugVal(s) === 'kontakt' || getSlugVal(s) === 'contact'
      );
      if (match) setPageData(match);
    });
  }, []);

  const title = pageData?.titel || 'Kontakt os';
  const badge = pageData?.undertitel || 'Dialog & Rådgivning';
  const intro =
    pageData?.intro ||
    'Hos PMB tror vi på, at de bedste løsninger opstår i dialog. Derfor er du altid velkommen til at række ud til os – uanset om du søger rådgivning, et uforpligtende tilbud eller reservedele.';

  const defaultSubject = useMemo(() => {
    if (presserParam) {
      return `Forespørgsel på: ${presserParam}`;
    }
    return '';
  }, [presserParam]);

  return (
    <>
      <Helmet>
        <title>PMB Kontakt – Presser, coil-linjer & centralsmøring</title>
        <meta
          name="description"
          content="Kontakt PMB for rådgivning, teknisk support og tilbud. Vi er specialister i presser, coil-linjer og centralsmøring – find telefon, e-mail og vores forhandlere her."
        />
        <meta property="og:title" content="PMB Kontakt – Presser, coil-linjer & centralsmøring" />
        <meta
          property="og:description"
          content="Hos PMB tror vi på, at de bedste løsninger opstår i dialog. Derfor er du altid velkommen til at række ud til os."
        />
      </Helmet>

      {/* Breadcrumb */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="hover:text-[#006368] transition">
            Forside
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="font-semibold text-stone-900">Kontakt</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#1a2024] text-white py-16 sm:py-24 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded bg-[#006368] text-white text-xs font-bold uppercase tracking-wider mb-4">
              {badge}
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              {title}
            </h1>
            <p className="mt-4 text-base sm:text-lg text-stone-300 leading-relaxed">
              {intro}
            </p>
            <p className="mt-2 text-sm sm:text-base text-stone-400 leading-relaxed">
              Vores teknikere og rådgivere står klar til at lytte og hjælpe dig videre. Vi vil rigtig gerne hjælpe dig og står altid til rådighed!
            </p>
          </div>
        </div>
      </section>

      {/* Main Content: Info cards + Form */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Column: Direct Info Cards */}
            <div className="lg:col-span-5 space-y-6">
              {/* Primary Contact Info Card */}
              <div className="bg-white rounded-lg border border-stone-200 p-6 sm:p-8 shadow-sm space-y-6">
                <h3 className="text-xl font-bold text-stone-900 border-b border-stone-100 pb-4">
                  Virksomhedsoplysninger
                </h3>

                <div className="space-y-5 text-sm">
                  {/* Firma */}
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded bg-[#006368]/10 text-[#006368] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-building" />
                    </span>
                    <div>
                      <span className="text-xs uppercase tracking-wider font-semibold text-stone-500 block">
                        Firma
                      </span>
                      <strong className="text-stone-900 text-base font-bold">
                        {companyInfo?.firmanavn || 'POVL MØLLERS MASKINFABRIK A/S'}
                      </strong>
                    </div>
                  </div>

                  {/* Telefon */}
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded bg-[#006368]/10 text-[#006368] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-phone" />
                    </span>
                    <div>
                      <span className="text-xs uppercase tracking-wider font-semibold text-stone-500 block">
                        Telefon (Ring på)
                      </span>
                      <a
                        href={`tel:${(companyInfo?.telefon || '57526262').replace(/\s+/g, '')}`}
                        className="text-lg font-extrabold text-[#006368] hover:text-[#004d51] transition whitespace-nowrap block"
                      >
                        {companyInfo?.telefon || '57 52 62 62'}
                      </a>
                      <span className="text-xs text-stone-500">eller +45 57 52 62 62</span>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded bg-[#006368]/10 text-[#006368] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-envelope" />
                    </span>
                    <div>
                      <span className="text-xs uppercase tracking-wider font-semibold text-stone-500 block">
                        E-mail (Skriv på)
                      </span>
                      <a
                        href={`mailto:${companyInfo?.email || 'info@pmborup.dk'}`}
                        className="text-base font-bold text-stone-900 hover:text-[#006368] transition whitespace-nowrap block"
                      >
                        {companyInfo?.email || 'info@pmborup.dk'}
                      </a>
                    </div>
                  </div>

                  {/* Adresse */}
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded bg-[#006368]/10 text-[#006368] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-location-dot" />
                    </span>
                    <div>
                      <span className="text-xs uppercase tracking-wider font-semibold text-stone-500 block">
                        Adresse & Værksted
                      </span>
                      <span className="text-stone-800 font-medium block">
                        {companyInfo?.adresse || 'Kimmerslevvej 3'}
                      </span>
                      <span className="text-stone-800 font-medium block">
                        {companyInfo?.postnr_by || '4140 Borup'}
                      </span>
                      <span className="text-xs text-stone-500">Danmark</span>
                    </div>
                  </div>

                  {/* CVR */}
                  <div className="flex items-start gap-4 pt-4 border-t border-stone-100">
                    <span className="w-10 h-10 rounded bg-stone-100 text-stone-600 flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-id-card" />
                    </span>
                    <div>
                      <span className="text-xs uppercase tracking-wider font-semibold text-stone-500 block">
                        CVR-nummer
                      </span>
                      <strong className="text-stone-800 font-mono text-base font-bold">
                        {companyInfo?.cvr || '52043018'}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-7">
              {presserParam && (
                <div className="mb-4 p-4 rounded-lg bg-[#006368]/10 border border-[#006368]/20 text-[#006368] flex items-center gap-3">
                  <i className="fa-solid fa-circle-info text-lg shrink-0" />
                  <div className="text-sm">
                    <span>Forespørgsel forudfyldt for maskine: </span>
                    <strong className="font-bold">{presserParam}</strong>
                  </div>
                </div>
              )}
              <ContactForm defaultSubject={defaultSubject} />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
